// MQTT SSL/TLS Session Priming Utility
// Fixes WebSocket connection issues by priming SSL/TLS sessions

export async function primeSSLConnection(mqttUrl, context = 'MQTT') {
  try {
    // Convert WSS URL to HTTPS for priming request
    const httpsUrl = mqttUrl.replace('wss://', 'https://').replace('/mqtt', '');
    
    console.log(`Priming SSL/TLS session for ${context}: ${httpsUrl}`);
    
    // Make silent HTTPS request to establish SSL/TLS session
    // Browser will cache this session for subsequent WSS connections
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    await fetch(httpsUrl, {
      method: 'HEAD',
      mode: 'no-cors', // Avoid CORS issues for priming request
      signal: controller.signal,
      cache: 'no-cache' // Don't cache the request, but cache the SSL session
    });
    
    clearTimeout(timeoutId);
    console.log(`SSL/TLS session primed successfully for ${context}`);
    
    // Small delay to ensure session is established
    await new Promise(resolve => setTimeout(resolve, 100));
    
    return true;
  } catch (error) {
    console.warn(`SSL priming failed for ${context}, continuing anyway:`, error.message);
    // Don't throw - connection might still work without priming
    return false;
  }
}

// Batch prime multiple URLs
export async function primeBatchSSLConnections(urls, context = 'Batch') {
  console.log(`Batch priming SSL sessions for ${context}...`);
  
  const results = await Promise.allSettled(
    urls.map(url => primeSSLConnection(url, context))
  );
  
  const successful = results.filter(r => r.status === 'fulfilled' && r.value === true).length;
  const failed = results.length - successful;
  
  console.log(`SSL batch priming complete: ${successful} success, ${failed} failed`);
  
  return {
    total: results.length,
    successful,
    failed,
    results
  };
}

// Auto-prime common MQTT URLs
const commonMqttUrls = [
  'wss://emqx.superagi.com:8084/mqtt'
];

// Automatic priming on module load (runs once per page load)
let autoPromedUrls = new Set();

export async function autoPrimeMqttConnections() {
  for (const url of commonMqttUrls) {
    if (!autoPromedUrls.has(url)) {
      await primeSSLConnection(url);
      autoPromedUrls.add(url);
    }
  }
}

// Run auto-priming after a short delay to avoid blocking app startup
if (typeof window !== 'undefined') {
  setTimeout(autoPrimeMqttConnections, 1000);
}
